import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { TestType } from '@risksmart-app/domain/src/types/consts/test-type';
import type { InsertControlTestOversightResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertControlTestOversightResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export type InsertControlTestOversightResultInput = {
  OversightAssessmentId: string;
  ControlIds: string[];
  Title?: string | null;
  TestType?: TestType | null;
  Description: string;
  DesignEffectiveness?: number | null;
  PerformanceEffectiveness?: number | null;
  OverallEffectiveness?: number | null;
  Submitter: string;
  TestDate: string;
  CustomAttributeData?: CustomAttributeData | null;
};

export const useInsertControlTestOversightResult = createMutationHook({
  name: 'insertControlTestOversightResult',
  isIdempotentCreate: true,
  mutationOptions: (trpc, onSuccess, requestContext) =>
    trpc.frontend.assessment.insertControlTestOversightResult.mutationOptions({
      onSuccess,
      trpc: { context: requestContext },
    }),
  mapInput: (input: InsertControlTestOversightResultInput) => input,
  invalidate: (trpc) => [
    // Apollo parity: evictField(cache, 'control_test_oversight_result')
    trpc.frontend.assessment.getOversightResultsByParentId.pathKey(),
    trpc.frontend.assessment.getAllOversightAssessmentResults.queryKey(),
    trpc.frontend.assessment.oversightResultById.pathKey(),
    trpc.frontend.testResult.controlTestOversightResultById.pathKey(),
    // Apollo parity: evictField(cache, 'control') — the control-scoped
    // oversight test-result reads.
    trpc.frontend.testResult.oversightAssessmentTestResultsByControlId.pathKey(),
    trpc.frontend.testResult.latestOversightAssessmentTestResultsByControlId.pathKey(),
    // Aggregate/count families.
    trpc.frontend.assessment.oversightAssessmentResultCount.queryKey(),
    // Apollo parity: evictField(cache, 'oversight_assessment')
    trpc.frontend.assessment.oversightActivityRegister.queryKey(),
    trpc.frontend.assessment.oversightActivityCount.queryKey(),
  ],
  mapResponse: (result: {
    Ids: string[];
  }): InsertControlTestOversightResultMutation => ({
    insertChildControlTestOversightResult: {
      __typename: 'insertControlTestResultOutput',
      Ids: result.Ids,
    },
  }),
  graphqlDocument: InsertControlTestOversightResultDocument,
  graphqlVariables: (input: InsertControlTestOversightResultInput) => input,
  evictFields: [
    'control_test_oversight_result',
    'control',
    'oversight_assessment',
  ],
});
